import { useQuery } from "@tanstack/react-query"

import supabase from "@/supabase"

/**
 * Interface for Depot data from Supabase
 */
export interface Depot {
  id: string
  depot_id?: string
  name?: string
  address?: string
  city?: string
}

export interface StageSegment {
  id: string
  [key: string]: string
}

export interface JobStage {
  id: string
  meta: {
    [key: string]: unknown
  }
  segments: StageSegment[]
}

/**
 * Interface for Job data from Supabase
 */
export interface Job {
  id: string
  title: string
  description: string
  url?: string
  person_type?: string
  shift_areas?: string[]
  sheriff_type?: string
  dspNames?: string
  depot_id?: string
  depot?: Depot
  vehicle_type?: string
  stages?: JobStage[]
  active: boolean
  hidden: boolean
  pass_mark_sjt?: string
  pass_mark_np?: string
  created_at?: string
  updated_at?: string
  location?: string
  type?: string
  salary?: string
  contract: string
}

// Type for raw job data from Supabase
type RawJobData = {
  id: string
  title: string
  description: string
  active: boolean
  hidden: boolean
  depot?: Depot
  person_type?: string
  shift_areas?: string[]
  url?: string
  sheriff_type?: string
  dsp_names?: string
  depot_id?: string
  vehicle_type?: string
  stages?: JobStage[]
  pass_mark_sjt?: string
  pass_mark_np?: string
  created_at?: string
  updated_at?: string
}

/**
 * Transform job data to match the format expected by the UI
 */
const transformJobData = (job: RawJobData): Job => ({
  ...(job as unknown as Job), // Cast to Job to handle all properties
  location: job.depot?.city + ", " + job.depot?.address || "Unknown",
  type: job.person_type || "Full-time",
})

/**
 * Hook for fetching all active jobs
 */
export const useFetchActiveJobs = () => {
  return useQuery({
    queryKey: ["jobs"],
    queryFn: async (): Promise<Job[]> => {
      try {
        const { data, error } = await supabase
          .from("jobs")
          .select("*, depot:depots(*)")
          .is("hidden", false)
          .eq("active", true)

        if (error) {
          console.error("Error fetching jobs:", error)
          throw new Error(error.message)
        }

        // Transform the data to match the format expected by the UI
        return (data || []).map(transformJobData)
      } catch (error) {
        console.error("Error in fetchJobs:", error)
        return []
      }
    },
  })
}

export const useFetchJobData = (jobId?: string) => {
  return useQuery({
    queryKey: ["job", jobId],
    queryFn: async (): Promise<Job> => {
      try {
        // If jobId is undefined or empty, return an empty job
        if (!jobId) {
          console.log("No job ID provided, returning empty job")
          return {} as Job
        }

        const { data, error } = await supabase
          .from("jobs")
          .select("*, depot:depots(*)")
          .eq("id", jobId)
          .single()

        console.log("Job data fetched:", data)
        if (error) {
          console.error("Error fetching job:", error)
          throw new Error(error.message)
        }
        // Transform the data to match the format expected by the UI
        return transformJobData(data)
      } catch (error) {
        console.error("Error in fetchJob:", error)
        return {} as Job
      }
    },
    // Only enable the query if jobId is defined
    enabled: !!jobId,
  })
}
